import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  createInspectionExport,
  createManagementReview,
  getInspectionExport,
  getInspectionExports,
  getManagementReviews,
  getValidationRequirements,
  getValidationSummary,
  recordValidationEvidence,
} from '../api/validationApi';
import type {
  ManagementReviewInput,
  ValidationEvidenceInput,
  ValidationRequirementFilters,
} from '../types/validation.types';

export const VALIDATION_QUERY_KEYS = {
  root: ['eqms-validation'] as const,
  summary: ['eqms-validation', 'summary'] as const,
  requirements: (filters: ValidationRequirementFilters) =>
    ['eqms-validation', 'requirements', filters] as const,
  reviews: ['eqms-validation', 'management-reviews'] as const,
  exports: ['eqms-validation', 'inspection-exports'] as const,
  export: (id: string) => ['eqms-validation', 'inspection-export', id] as const,
};

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'Validation request failed';
}

export function useValidationSummary() {
  return useQuery({
    queryKey: VALIDATION_QUERY_KEYS.summary,
    queryFn: getValidationSummary,
  });
}

export function useValidationRequirements(filters: ValidationRequirementFilters) {
  return useQuery({
    queryKey: VALIDATION_QUERY_KEYS.requirements(filters),
    queryFn: () => getValidationRequirements(filters),
  });
}

export function useRecordValidationEvidence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ requirementId, input }: {
      requirementId: string;
      input: ValidationEvidenceInput;
    }) => recordValidationEvidence(requirementId, input),
    onSuccess: (result) => {
      toast.success(
        result.evidence.result === 'pass'
          ? `${result.requirement.requirementCode} verified`
          : `${result.requirement.requirementCode} recorded as a gap`,
      );
      void queryClient.invalidateQueries({ queryKey: VALIDATION_QUERY_KEYS.root });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
}

export function useManagementReviews() {
  return useQuery({
    queryKey: VALIDATION_QUERY_KEYS.reviews,
    queryFn: () => getManagementReviews(),
  });
}

export function useCreateManagementReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ManagementReviewInput) => createManagementReview(input),
    onSuccess: () => {
      toast.success('Management review snapshot approved');
      void queryClient.invalidateQueries({ queryKey: VALIDATION_QUERY_KEYS.root });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
}

export function useInspectionExports() {
  return useQuery({
    queryKey: VALIDATION_QUERY_KEYS.exports,
    queryFn: getInspectionExports,
  });
}

export function useCreateInspectionExport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createInspectionExport,
    onSuccess: () => {
      toast.success('Inspection package frozen with a SHA-256 digest');
      void queryClient.invalidateQueries({ queryKey: VALIDATION_QUERY_KEYS.root });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
}

export function useInspectionExport(id: string) {
  return useQuery({
    queryKey: VALIDATION_QUERY_KEYS.export(id),
    queryFn: () => getInspectionExport(id),
    enabled: Boolean(id),
  });
}
